import { generateRandomNumber } from '@/utils/numbers.util';

// export const TW_CLASS_BG_BACKDROP = 'bg-neutral-950/50 backdrop-blur-sm';

export const topicsColors = ['#718DBC', '#924CEC', '#58B2CB', '#6AB586'];

type NebulyPalette =
  | 'yellow'
  | 'blue'
  | 'violet'
  | 'red'
  | 'pink'
  | 'violet2'
  | 'viole-light'
  | 'violet-platform'
  | 'violet-platform-light'
  | 'green'
  | 'green2'
  | 'link'
  | 'main_blue'
  | 'blue_dark'
  | 'neutral';

export type NebulyTwColor = `n-${NebulyPalette}`;

/**
 * Nebuly "general palette".
 *
 * WARNING:
 * some colors does not have an official name,
 * so I just give them a random name
 *
 * @see the figma main mockup
 */
export const nebulyPalette = {
  yellow: '#D6EC4C',
  blue: '#4F79BF',
  violet: '#924CEC',
  red: '#CB6060',
  pink: '#FB67CA',
  violet2: '#9B88ED',
  'viole-light': '#A92AE5',
  // violet_platform: '#8743E9',
  'violet-platform': '#606ACB',
  'violet-platform-light': '#8C93D9',
  green: '#5EFCE8',
  green2: '#04BFDA',
  link: '#3992EE',
  main_blue: '#0A4FFF',
  blue_dark: '#3862A8',
  neutral: '#F1B74D',
} as const satisfies Record<NebulyPalette, string>;

export function generateRandomRgbColor() {
  return `rgb(${generateRandomNumber(50, 255)}, ${generateRandomNumber(50, 255)}, ${generateRandomNumber(50, 255)})`;
}

// extract hex from bg-n-violet-platform
export function extractHex(color: string) {
  return color.match(/#(?:[0-9a-fA-F]{3}){1,2}/g)?.[0] ?? '';
}

/**
 * Loop through the colors from the official palette
 *
 * if we reach the end of the palette, we start from the beginning
 */
export function loopColorsFromOfficialPalette(index: number): () => string {
  const colors = Object.values(nebulyPalette);
  return () => colors[index % colors.length];

  // using closure to keep the index
  // return () => {
  //   const color = colors[i];
  //   index = (i + 1) % colors.length;
  //   return color;
  // };
}

/**
 * return an rgba color with the given opacity
 *
 * @param rgb
 * @param opacity
 */
export function addOpacityToRgb(rgb: string, opacity: number) {
  const digits = rgb.match(/\d+/g);
  const hasAlreadyOpacity = rgb.includes('rgba');

  // check if the hex color already has an opacity, e.g. #000000ff
  if (hasAlreadyOpacity) {
    return `rgba(${digits?.[0]}, ${digits?.[1]}, ${digits?.[2]}, ${opacity})`;
  }

  // get digits from the string
  return `rgba(${digits?.[0]}, ${digits?.[1]}, ${digits?.[2]}, ${opacity})`;
}

export function addOpacityToHex(hex: string, opacity: number) {
  return `${hex}${Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0')}`;
}

export function convertHexToRgb(hex: string) {
  let normalizedHex = hex;
  if (hasHexOpacity(hex)) {
    normalizedHex = stripHexOpacity(hex);
  }

  const hexColorComponents = normalizedHex.replace('#', '');
  const r = parseInt(hexColorComponents.substring(0, 2), 16);
  const g = parseInt(hexColorComponents.substring(2, 4), 16);
  const b = parseInt(hexColorComponents.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b})`;
}

export function stripHexOpacity(color: string) {
  return color.slice(0, 7);
}

export function hasHexOpacity(color: string) {
  return color.length === 9;
}

export function isHexColor(color: string) {
  return color.startsWith('#');
}

/**
 * Add opacity to a color
 * in case the color is a hex color, it will convert it to rgba
 *
 * @param color
 * @param opacity
 */
export function addOpacityToColor(color: string | string[] | undefined, opacity: number) {
  if (!color) return '';

  // sometimes the color is in the format ['#hex']
  if (Array.isArray(color)) {
    if (color.length === 0) return '';

    if (isHexColor(color[0])) {
      const rgb = convertHexToRgb(color[0]);
      return addOpacityToRgb(rgb, opacity);
    }

    return '';
  }

  if (isHexColor(color)) {
    const rgb = convertHexToRgb(color);
    return addOpacityToRgb(rgb, opacity);
  }

  return addOpacityToRgb(color, opacity);
}
